import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../api/axiosBaseQuery';

// Types aligned with services/chatBotsService.ts
export type Channel = 'instagram' | 'whatsapp' | 'email';

export type ChatBot = {
  id: number;
  name: string;
  description: string;
  bot_type: string;
  bot_model: string;
  system_prompt: string | null;
  welcome_message: string | null;
  fallback_message: string | null;
  channels: string[] | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  pricing_type?: string;
  schedule_mode?: 'always' | 'disabled' | 'scheduled';
  schedule?: any | null;
  enabled_mcps?: string[];
};

export type BotStats = {
  bot_id: number;
  name: string;
  messages_sent: number;
  messages_received: number;
  avg_response_time_ms: number | null;
  last_used_at: string | null;
  unique_users: number;
  appointments_booked: number;
  total_tokens: number;
  total_cost_usd: number;
};

export type ChannelsStatus = { instagram: boolean; whatsapp: boolean; email: boolean };
export type ActiveByChannel = { instagram: ChatBot | null; whatsapp: ChatBot | null; email: ChatBot | null };
export type PricingPlan = { key: string; name: string; description: string | null };
export type UsageMap = Record<number, number>;

export type GeneralStatItem = {
  current: number;
  previous: number;
  percentage_change: number | null;
};

export type GeneralStats = {
  total_messages_handled: GeneralStatItem;
  appointments_booked: GeneralStatItem;
  unique_users_interacted: GeneralStatItem;
};
function ck() {
  try {
    return localStorage.getItem('comercio_key') || null;
  } catch {
    return null;
  }
}

export const botsApi = createApi({
  reducerPath: 'botsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Bots', 'ChannelsStatus', 'ActiveByChannel', 'Plans', 'Usage'],
  endpoints: (builder) => ({
    getBots: builder.query<ChatBot[], void>({
      query: () => ({
        url: '/v1/chat-bots',
        method: 'GET',
        params: ck() ? { comercio_key: ck() } : undefined,
      }),
      transformResponse: (data: any) => {
        return Array.isArray(data?.data) ? (data.data as ChatBot[]) : Array.isArray(data) ? data : [];
      },
      providesTags: ['Bots'],
    }),
    getChannelsStatus: builder.query<ChannelsStatus, void>({
      query: () => ({
        url: '/v1/chat-bots/channels/status',
        method: 'GET',
        params: ck() ? { comercio_key: ck() } : undefined,
      }),
      transformResponse: (data: any) => data || { instagram: false, whatsapp: false, email: false },
      providesTags: ['ChannelsStatus'],
    }),
    getActiveByChannel: builder.query<ActiveByChannel, void>({
      query: () => ({
        url: '/v1/chat-bots/active-by-channel',
        method: 'GET',
        params: ck() ? { comercio_key: ck() } : undefined,
      }),
      transformResponse: (data: any) => data || { instagram: null, whatsapp: null, email: null },
      providesTags: ['ActiveByChannel'],
    }),
    getPricingPlans: builder.query<PricingPlan[], void>({
      query: () => ({ url: '/v1/chat-bots/pricing-plans', method: 'GET' }),
      transformResponse: (data: any) => {
        if (Array.isArray(data)) {
          return data.map((p: any) => ({
            key: String(p.key || p.plan_key || 'base'),
            name: String(p.name || p.plan_key || 'Base'),
            description: p.description ?? null,
          })) as PricingPlan[];
        }
        return [{ key: 'base', name: 'Base', description: 'Plan base por defecto' }];
      },
      providesTags: ['Plans'],
    }),
    getUsage: builder.query<UsageMap, void>({
      query: () => ({
        url: '/v1/chat-bots/usage',
        method: 'GET',
        params: ck() ? { comercio_key: ck() } : undefined,
      }),
      transformResponse: (data: any) => (data?.usage || {}) as UsageMap,
      providesTags: ['Usage'],
    }),
    getStats: builder.query<BotStats[], { time_range?: string } | void>({
      query: (arg) => ({
        url: '/v1/chat-bots/stats',
        method: 'GET',
        params: {
          comercio_key: ck() || undefined,
          ...(arg && arg.time_range ? { time_range: arg.time_range } : {})
        },
      }),
      providesTags: ['Bots'],
    }),
    getGeneralStats: builder.query<GeneralStats, { time_range?: string } | void>({
      query: (arg) => ({
        url: '/v1/chat-bots/general-stats',
        method: 'GET',
        params: {
          comercio_key: ck() || undefined,
          ...(arg && arg.time_range ? { time_range: arg.time_range } : {})
        },
      }),
      providesTags: ['Bots'],
    }),

    createBot: builder.mutation<ChatBot, {
      name: string;
      description?: string;
      bot_type?: string;
      bot_model?: string;
      system_prompt?: string | null;
      welcome_message?: string | null;
      fallback_message?: string | null;
      channels?: string[] | null;
      is_active?: boolean;
      pricing_type?: string;
      schedule_mode?: 'always' | 'disabled' | 'scheduled';
      schedule?: any | null;
      enabled_mcps?: string[];
    }>({
      query: (payload) => ({
        url: '/v1/chat-bots',
        method: 'POST',
        data: { comercio_key: ck() || undefined, ...payload },
      }),
      invalidatesTags: ['Bots', 'ActiveByChannel', 'Usage'],
    }),

    updateBot: builder.mutation<ChatBot, {
      id: number;
      payload: Partial<Omit<ChatBot, 'id' | 'created_at' | 'updated_at'>> & {
        system_prompt?: string | null;
        welcome_message?: string | null;
        fallback_message?: string | null;
        schedule_mode?: 'always' | 'disabled' | 'scheduled';
        schedule?: any | null;
        enabled_mcps?: string[];
      };
    }>({
      query: ({ id, payload }) => ({
        url: `/v1/chat-bots/${id}`,
        method: 'PUT',
        data: { comercio_key: ck() || undefined, ...payload },
      }),
      invalidatesTags: ['Bots', 'ActiveByChannel'],
    }),

    removeBot: builder.mutation<{ success: boolean }, { id: number }>({
      query: ({ id }) => ({
        url: `/v1/chat-bots/${id}`,
        method: 'DELETE',
        params: ck() ? { comercio_key: ck() } : undefined,
      }),
      invalidatesTags: ['Bots', 'ActiveByChannel', 'Usage'],
    }),

    activateForChannel: builder.mutation<any, { id: number; channel: Channel }>({
      query: ({ id, channel }) => ({
        url: `/v1/chat-bots/${id}/activate-for-channel`,
        method: 'POST',
        data: { channel, comercio_key: ck() || undefined },
      }),
      invalidatesTags: ['ActiveByChannel'],
    }),

    clearActiveForChannel: builder.mutation<any, { channel: Channel }>({
      query: ({ channel }) => ({
        url: `/v1/chat-bots/clear-active-for-channel`,
        method: 'POST',
        data: { channel, comercio_key: ck() || undefined },
      }),
      invalidatesTags: ['ActiveByChannel'],
    }),
  }),
});

// Hooks
export const {
  useGetBotsQuery,
  useGetChannelsStatusQuery,
  useGetActiveByChannelQuery,
  useGetPricingPlansQuery,
  useGetUsageQuery,
  useGetStatsQuery,
  useGetGeneralStatsQuery,
  useCreateBotMutation,
  useUpdateBotMutation,
  useRemoveBotMutation,
  useActivateForChannelMutation,
  useClearActiveForChannelMutation,
} = botsApi;